import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../config/config";

// Initiate escrow for advance payment
export const InitiateEscrow = createAsyncThunk(
  "escrow/initiate",
  async ({ escrowData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/escrow/initiate-advance`, escrowData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to initiate escrow" });
    }
  }
);

// Verify escrow payment (after SafePay callback)
export const VerifyEscrowPayment = createAsyncThunk(
  "escrow/verify",
  async ({ verificationData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/escrow/verify-payment`, verificationData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to verify payment" });
    }
  }
);

// Confirm and release escrow to landlord
export const ConfirmEscrowRelease = createAsyncThunk(
  "escrow/confirmRelease",
  async ({ escrowId, landlordId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/escrow/confirm-release`, {
        escrowId,
        landlordId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to release escrow" });
    }
  }
);

// Get escrow transaction details
export const GetEscrowDetails = createAsyncThunk(
  "escrow/getDetails",
  async ({ escrowId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/escrow/transaction/${escrowId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch escrow details" });
    }
  }
);

// Get tenant's escrow history
export const GetTenantEscrowHistory = createAsyncThunk(
  "escrow/getTenantHistory",
  async ({ tenantId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/escrow/tenant/${tenantId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch escrow history" });
    }
  }
);

// Get landlord's escrow history
export const GetLandlordEscrowHistory = createAsyncThunk(
  "escrow/getLandlordHistory",
  async ({ landlordId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/escrow/landlord/${landlordId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch escrow history" });
    }
  }
);

const EscrowTransactionSlice = createSlice({
  name: "escrow",
  initialState: {
    loading: false,
    transactions: [],
    currentTransaction: null,
    escrowStatus: null, // 'pending', 'held', 'verified', 'released'
    checkoutUrl: null,
    error: "",
    success: false,
  },
  reducers: {
    resetEscrowState: (state) => {
      state.loading = false;
      state.error = "";
      state.success = false;
      state.currentTransaction = null;
      state.checkoutUrl = null;
      state.escrowStatus = null;
    },
    setEscrowStatus: (state, action) => {
      state.escrowStatus = action.payload;
    },
    clearEscrowError: (state) => {
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    // Initiate Escrow
    builder.addCase(InitiateEscrow.pending, (state) => {
      state.loading = true;
      state.error = "";
      state.success = false;
    });
    builder.addCase(InitiateEscrow.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.currentTransaction = action.payload?.data?.transaction;
      state.checkoutUrl = action.payload?.data?.checkoutUrl;
      state.escrowStatus = 'pending';
    });
    builder.addCase(InitiateEscrow.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to initiate escrow";
    });

    // Verify Escrow Payment
    builder.addCase(VerifyEscrowPayment.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(VerifyEscrowPayment.fulfilled, (state, action) => {
      state.loading = false;
      state.currentTransaction = action.payload?.data;
      state.escrowStatus = 'held';
      state.success = true;
    });
    builder.addCase(VerifyEscrowPayment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Payment verification failed";
      state.escrowStatus = 'failed';
    });

    // Confirm Escrow Release
    builder.addCase(ConfirmEscrowRelease.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(ConfirmEscrowRelease.fulfilled, (state, action) => {
      state.loading = false;
      state.escrowStatus = 'released';
      state.currentTransaction = action.payload?.data;
      // Update in transactions list
      const releasedId = action.payload?.data?._id;
      state.transactions = state.transactions.map(t =>
        t._id === releasedId ? { ...t, status: 'released_to_landlord' } : t
      );
    });
    builder.addCase(ConfirmEscrowRelease.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to release escrow";
    });

    // Get Escrow Details
    builder.addCase(GetEscrowDetails.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetEscrowDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.currentTransaction = action.payload?.data;
      state.escrowStatus = action.payload?.data?.status;
    });
    builder.addCase(GetEscrowDetails.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch escrow details";
    });

    // Get Tenant Escrow History
    builder.addCase(GetTenantEscrowHistory.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetTenantEscrowHistory.fulfilled, (state, action) => {
      state.loading = false;
      state.transactions = action.payload?.data || [];
    });
    builder.addCase(GetTenantEscrowHistory.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch escrow history";
    });

    // Get Landlord Escrow History
    builder.addCase(GetLandlordEscrowHistory.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetLandlordEscrowHistory.fulfilled, (state, action) => {
      state.loading = false;
      state.transactions = action.payload?.data || [];
    });
    builder.addCase(GetLandlordEscrowHistory.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch escrow history";
    });
  },
});

export const { resetEscrowState, setEscrowStatus, clearEscrowError } = EscrowTransactionSlice.actions;
export default EscrowTransactionSlice.reducer;
